import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MetadataService } from "@/services/metadata";
import type { OptionsResponse } from "@/types/dynamic-detail";

type CRMContextType = {
    metadataCache: Record<string, OptionsResponse | null | undefined>;
    ensureLoaded: (objectKey: string) => Promise<OptionsResponse>;
    preload: (objectKeys: string[]) => Promise<void>;
    refresh: (objectKey: string) => Promise<void>;
    clearCache: () => void;
};

const CRMContext = React.createContext<CRMContextType>(null!);

export function CRMProvider({ children }: { children: React.ReactNode }) {
    const [metadataCache, setMetadataCache] = useState<Record<string, OptionsResponse | null | undefined>>({});
    const inFlight = useRef<Record<string, Promise<OptionsResponse> | undefined>>({});

    const load = async (objectKey: string): Promise<OptionsResponse> => {
        const endpoint = `/crm/objects/${objectKey}/`;
        const existingPromise = inFlight.current[objectKey];
        if (existingPromise) return existingPromise;

        const p = MetadataService.get(endpoint)
            .then(resp => {
                setMetadataCache(prev => ({ ...prev, [objectKey]: resp as OptionsResponse }));
                return resp as OptionsResponse;
            })
            .finally(() => {
                delete inFlight.current[objectKey];
            });
        inFlight.current[objectKey] = p;
        return p;
    };

    const ensureLoaded = async (objectKey: string) => {
        const cached = metadataCache[objectKey];
        if (cached) return cached as OptionsResponse;
        return await load(objectKey);
    };

    const refresh = async (objectKey: string) => {
        // Clear cache entry and reload
        setMetadataCache(prev => ({ ...prev, [objectKey]: undefined }));
        await load(objectKey);
    };

    const preload = async (objectKeys: string[]) => {
        await Promise.all(
            objectKeys.map(async key => {
                if (!metadataCache[key]) {
                    await ensureLoaded(key);
                }
            })
        );
    };

    const clearCache = () => {
        setMetadataCache({});
        MetadataService.clearCache();
        window.location.reload();
    };

    const value = useMemo(() => ({ metadataCache, ensureLoaded, preload, refresh, clearCache }), [metadataCache]);

    useEffect(() => {
        preload([
            "certification-registrations",
            "cases",
            "accounts",
            "opportunities",
            "invoices",
            "leads",
            "injection-markers",
            "contacts",
            "users",
            "marketing-campaign-insights",
            "events",
            "calls",
            "recycling-markers",
            "tasks",
            "invoice-groups",
            "mailing-campaigns",
            "marketing-campaigns",
            "flash-injections",
            "installment-schedules"
        ]);
    }, []);

    return <CRMContext.Provider value={value}>{children}</CRMContext.Provider>;
}

export function useCRM() {
    return useContext(CRMContext);
}

export function useCRMMetadata(objectKey: string): { metadata: OptionsResponse | null | undefined; loading: boolean } {
    const { metadataCache, ensureLoaded } = useCRM();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let mounted = true;
        if (metadataCache[objectKey] === undefined) {
            setLoading(true);
            ensureLoaded(objectKey)
                .catch(() => { })
                .finally(() => {
                    if (mounted) setLoading(false);
                });
        }
        return () => {
            mounted = false;
        };
    }, [objectKey, metadataCache, ensureLoaded]);

    return { metadata: metadataCache[objectKey], loading };
}
